import listIcon from '@disy/cadenza-icons/list.svg';
import h from 'hyperscript';

import { Select } from 'ui/select/select';

import { errorAlert } from 'cadenza/alert';
import { assert } from 'cadenza/utils/custom-error';
import { hashCode } from 'cadenza/utils/hash-code';
import { logger } from 'cadenza/utils/logging';
import { TableConditionApi } from 'cadenza/api-client/conditions/table-condition-api';
import { icon } from 'cadenza/utils/icon/icon';
import 'cadenza/public-path';
import { ConditionElement } from 'cadenza/conditions/condition-element';

import './table-condition.css';
import i18n from './messages.properties';

const DEFAULT_LIMIT = 30;

export class TableCondition extends ConditionElement {

  #columnsConfigSupplier;
  #conditionApiSupplier;
  #select;
  #sortDescription;
  #values;
  allowExcludeValues;
  #excludeValues;
  #latestSetValuesPromise; // for keeping consistency for other conditions, see onConditionChange

  #labelledBy;
  #describedBy;

  #dialogIsOpening = false;

  /*
  * holds the maxSelection between construction and connectedCallback; after that "max-selection"
  * HTML attribute (available via 'get maxSelection' in the ConditionElement super-class) is single
  * source of truth
  * */
  #maxSelection;

  constructor ({
    columnsConfigSupplier = data => data.tableColumnsDefinition,
    conditionApiSupplier = configObject => new TableConditionApi(configObject),
    sortDescription,
    values,
    allowExcludeValues = false,
    excludeValues = false,
    maxSelection = -1
  } = {}) {
    super();
    this.#columnsConfigSupplier = columnsConfigSupplier;
    this.#conditionApiSupplier = conditionApiSupplier;
    this.#sortDescription = sortDescription;
    this.#values = values?.valueItems;
    this.#maxSelection = maxSelection;
    this.allowExcludeValues = allowExcludeValues;
    this.#excludeValues = excludeValues;
    this.labelledBy = this.getAttribute('aria-labelledby');
  }

  connectedCallback () {
    super.connectedCallback();
    this.innerHTML = '';
    this.classList.add('table-condition');
    // if constructed with <d-table-element> instead of an explicit constructor call,
    // the max-selection set via HTML attribute must not be overridden
    if (!this.hasAttribute('max-selection')) {
      this.setAttribute('max-selection', this.#maxSelection);
    }

    const data = this.previousElementSibling?.value;
    this._tableColumnsDef = this.#columnsConfigSupplier(data);

    this.conditionApi = this.#conditionApiSupplier({
      conditionSetId: this.conditionSetId,
      conditionName: this.conditionName,
      conditionId: this.conditionId,
      conditionValuesSetHashGetter: () => this.conditionValuesSetHash
    });

    let selectData;
    if (this.removeOnly) {
      selectData = data.value.map(({ values, text }) => {
        return {
          id: String(hashCode(JSON.stringify(values))),
          text,
          value: values,
          selected: true
        };
      });
    }

    if (!this.custom && selectData?.length === 0 && this.removeOnly) {
      // This happens for drill through conditions, which are not set.
      return;
    }

    this.#select = new Select({
      allowClear: true,
      query: this.removeOnly
        ? null
        : getQuery(
          this.conditionApi, DEFAULT_LIMIT, this.sortDescription, this.createRowConverter()
        ),
      elementRight: this.removeOnly ? null : this._createDialogButton(),
      maxSelection: this.maxSelection,
      moreLessLinks: true,
      multiple: this.multiple,
      removeOnly: this.removeOnly,
      data: selectData
    });
    this.#select.disabled = !this.enabled;
    this.#select.labelledBy = this.labelledBy;
    this.#select.describedBy = this.describedBy;
    this.#select.addEventListener('change', () => {
      this.onSelectionChange();
      this.onConditionChange();
    });

    this.append(this.#select);

    this.defaultValue = this.prepareRows(data?.defaultValue || []);
    this.value = this.prepareRows(this.#values || data?.value || []);
    this._updateExcludeMode();
    this.initChanged();
  }

  /**
   * the function used to format a row for display in select2s results
   *
   * @protected
   * @return {Function} a function, which takes a row as parameter and returns the text
   * for display by select2
   */
  createRowConverter () {
    return row => getTextFromVisibleValues(
      row.formatted,
      this._tableColumnsDef.columns
    );
  }

  _createDialogButton () {
    const dialogButton = h('button.dialog-trigger.button.button-icon.button-borderless', {
      title: i18n('dialog-trigger.title'),
      onclick: event => {
        event.preventDefault();
        if (this.#dialogIsOpening) {
          return;
        }
        this.#dialogIsOpening = true;
        this.showDialog()
          .finally(() => {
            this.#dialogIsOpening = false;
          });
      }
    }, icon(listIcon));

    dialogButton.disabled = !this.enabled;

    return dialogButton;
  }

  /**
   * Allows modification of the rows before setting them in the select2 (for example select 2 needs
   * the items to have an id)
   *
   * @protected
   * @param {object[]} rows - the initial rows
   * @return {object[]} the modified rows
   */
  prepareRows (rows) {
    return rows.map(row => {
      row.value = row.values;
      row.id = String(hashCode(JSON.stringify(row.values)));
      return row;
    });
  }

  set value (rows) {
    this.#select.selection = rows;
    this.onSelectionChange();
  }

  get value () {
    return this.#select?.selection;
  }

  get numberOfConditionValueRows () {
    return this.value?.length ?? 0;
  }

  get multiple () {
    return (this.maxSelection !== 1);
  }

  get persistenceId () {
    return this.getAttribute('persistence-id');
  }

  get enabled () {
    return super.enabled;
  }

  set enabled (enabled) {
    super.enabled = enabled;
    this.#select.disabled = !enabled;
  }

  get isEmpty () {
    const currentValue = this.value;
    return this.multiple ? currentValue.length < 1 : currentValue == null;
  }

  get sortDescription () {
    return this.#sortDescription || this.previousElementSibling.value?.sortDescription;
  }

  reset (triggerConditionChange = true) {
    this.#excludeValues = false;
    super.reset(triggerConditionChange);
    this._updateExcludeMode();
  }

  /**
   * Returns a promise that resolves when the dialog closes.
   *
   * @return {Promise<{ConditionTableModal?: ConditionTableModal}>} resolves when dialogue closes.
   */
  showDialog () {
    return import(/* webpackChunkName: "condition-table-modal" */ './condition-table-modal/condition-table-modal')
      .then(({ ConditionTableModal }) => {
        const dialog = new ConditionTableModal({
          title: this.conditionLabel,
          conditionApi: this.conditionApi,
          selectedRows: this.value,
          maxSelection: this.maxSelection,
          conditionId: this.conditionId,
          tableColumnsDef: this._tableColumnsDef,
          allowExcludeValues: this.allowExcludeValues,
          excludeValues: this.#excludeValues
        });
        dialog.addEventListener('close', () => {
          dialog.remove();
          const dialogValue = dialog.returnValue;

          if (this.allowExcludeValues && dialogValue) {
            this.value = dialogValue.selectedItems;
            this.#excludeValues = dialogValue.excludeValues;
            this._updateExcludeMode();
            this.onConditionChange(true);
          }

          if (!this.allowExcludeValues && dialogValue) {
            this.value = dialogValue;
            this.onConditionChange(false);
          }
        });
        document.body.appendChild(dialog);
        dialog.showModal();
      }).catch((error) => {
        logger.error('Failed to show table condition modal', error);
        errorAlert(
          i18n('error.dialog.title'),
          i18n('error.dialog.message'));
      });
  }

  /**
   * Listener called when the selection has changed in the table condition
   *
   * @protected
   */
  onSelectionChange () {
    // do nothing
  }

  onConditionChange () {
    // the filter controller is not available in workbooks
    this.filterController?.disableOtherConditionsIfNeededForConsistency([ this ]);
    const rows = this.value.map(row => row.value);

    const setValuesPromise = (this.allowExcludeValues
      ? this.conditionApi.setValueAndExcludeValuesMode(rows, this.#excludeValues)
      : this.conditionApi.setValue(rows));
    this.#latestSetValuesPromise = setValuesPromise;
    setValuesPromise
      .then(json => {
        return super.onConditionChange(json);
      })
      .catch(error => {
        this.handleConditionValueChangeError(error);
      })
      .finally(() => {
        // if the changes are not fully processed yet, disable the other conditions again.
        const enable = setValuesPromise === this.#latestSetValuesPromise;
        // the filter controller is not availabe in workbooks
        this.filterController?.setConditionsDisabledForConsistency(enable, this);
      });
  }

  /**
   * @override
   * @return {{label: *, execute: (function(): void)}[]} - Menu actions
   */
  getSpecificActionsForWorkbooks () {
    if (this.allowExcludeValues && this.enabled) {
      return [ {
        label: this.#excludeValues ? i18n('includeValues') : i18n('excludeValues'),
        execute: () => this._toggleExcludeValues()
      } ];
    } else {
      return [];
    }
  }

  _toggleExcludeValues () {
    this.#excludeValues = !this.#excludeValues;
    this.onConditionChange(true);
  }

  _updateExcludeMode () {
    this.classList.toggle('is-exclude-values', this.#excludeValues);
  }

  set excludeValues (excludeValues) {
    this.#excludeValues = excludeValues;
    this._updateExcludeMode();
  }

  get excludeValues () {
    return this.#excludeValues;
  }

  set labelledBy (elementOrId) {
    this.#labelledBy = elementOrId?.id || elementOrId;
    if (this.#select) {
      this.#select.labelledBy = this.#labelledBy;
    }
  }

  get labelledBy () {
    return this.#labelledBy;
  }

  set describedBy (elementOrId) {
    this.#describedBy = elementOrId?.id || elementOrId;
    if (this.#select) {
      this.#select.describedBy = this.#describedBy;
    }
  }

  get describedBy () {
    return this.#describedBy;
  }

  focus () {
    this.#select.focus();
  }

}

customElements.define('d-table-condition', TableCondition);

function getQuery (conditionApi, limit, sortDescription, rowConverter) {
  return ({ term, page = 1 }, callback) => {
    const offset = (page - 1) * limit;
    const options = Object.assign({
      offset,
      limit,
      filterText: term,
      noCount: true
    }, sortDescription);
    conditionApi.getConditionTable(options).then(res => {
      // select2 requires a callback call here
      callback({
        results: res.rows.map(row => ({
          id: String(hashCode(JSON.stringify(row.values))),
          text: rowConverter(row),
          value: row.values
        })),
        pagination: {
          more: (offset + limit < res.rowsTotal)
        }
      });
    }).catch(err => {
      if (err.name !== 'AbortError') {
        logger.error('Failed to load condition table', err);
      }
    });
  };
}

export function getTextFromVisibleValues (values, configurationDefColumns) {
  assert(Array.isArray(values), 'Values must be an array');
  assert(Array.isArray(configurationDefColumns), 'Columns must be an array');
  assert(
    values.length === configurationDefColumns.length,
    'value and configuration column should match'
  );
  return values.filter((value, i) => (value !== null && value !== undefined) && configurationDefColumns[i].visible).join(', ');
}
